import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiError } from "@/lib/api";
import NoIndex from "@/components/NoIndex";

export default function AdminLogin() {
  const { login, isAuthenticated, checking } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!checking && isAuthenticated) navigate("/admin/dashboard", { replace: true });
  }, [isAuthenticated, checking, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(username, password);
      toast.success("Benvenuto");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err) || t("admin.invalid_credentials"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-afb-bg px-6 py-16">
      <NoIndex />
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-afb-muted hover:text-afb-gold mb-6"
          data-testid="back-to-site"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("admin.back_to_site")}
        </Link>

        <Card className="border-[color:var(--afb-cream)]">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 rounded-full bg-afb-cream flex items-center justify-center text-afb-gold">
                <Lock className="w-5 h-5" />
              </span>
              <div>
                <h1 className="serif text-2xl text-afb-ink leading-tight">
                  {t("admin.login_title")}
                </h1>
                <p className="text-xs text-afb-muted uppercase tracking-[0.22em] mt-1">
                  {t("admin.login_subtitle")}
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <Label htmlFor="username" className="text-xs uppercase tracking-widest text-afb-muted">
                  {t("admin.username")}
                </Label>
                <Input
                  id="username"
                  data-testid="admin-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-afb-muted">
                  {t("admin.password")}
                </Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-2"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="w-full bg-afb-ink hover:bg-black text-white rounded-full h-11"
              >
                {submitting ? "..." : t("admin.sign_in")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
